import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { ProductRoleService } from './services/product-role.service';
import { Subscription, forkJoin, Subject, combineLatest, of } from 'rxjs';

import { AppComponentService } from './services/app-component.service';
import { WorksiteBasicinfoService } from './worksites/worksite/details/basicinfo/worksite-basicinfo.service';
import { PlanBasicinfoService } from './plans/plan/details/basicinfo/plan-basicinfo.service';
import { InvoicingProposalBasicinfoService } from './invoicing-proposals/invoicing-proposal/details/basicinfo/invoicing-proposal-basicinfo.service';
import { TenderingBasicinfoService } from './tenderings/tendering/details/basicinfo/tendering-basicinfo.service';
import { CollectionInvoicingProposalBasicinfoService } from './invoicing-proposals/collection-invoicing-proposal/details/basicinfo/collection-invoicing-proposal-basicinfo.service';
import { EndCustomerInvoiceBasicInformationService } from './end-customer-invoices/end-customer-invoice/details/basicinfo/end-customer-invoice-basicinfo.service';

@Component({
    selector: 'app-open-target',
    templateUrl: './open-target.component.html',
    styleUrls: ['./open-target.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpenTargetComponent implements OnInit, OnDestroy {

    public searchId: string = '';
    public searchReady: boolean = false;
    private accessToInvoicingProposals: boolean = false;
    private accessToEndCustomerInvoice: boolean = false;
    private accessToCollectionInvoicingProposals: boolean = false;
    private accessToTenderings: boolean = false;
    public subscriptions: Array<Subscription> = [];
    private searchTarget = '';


    constructor(private router: Router,
        private route: ActivatedRoute,
        private roleService: ProductRoleService,
        private appComponentService: AppComponentService,
        private worksiteBasicInfoService: WorksiteBasicinfoService,
        private planBasicInfoService: PlanBasicinfoService,
        private invoicingProposalBasicInfoService: InvoicingProposalBasicinfoService,
        private endCustomerInvoiceService: EndCustomerInvoiceBasicInformationService,
        private collectionInvoicingProposalBasicInfoService: CollectionInvoicingProposalBasicinfoService,
        private tenderingBasicInfoService: TenderingBasicinfoService,
        private cdRef: ChangeDetectorRef) {

        this.subscriptions.push(
            combineLatest([this.route.params,
            this.roleService.userHasRole('IwmAllowedToAccessInvoicingProposals'),
            this.roleService.userHasRole('IwmAllowedToAccessTenderingsAndBids'),
            this.roleService.userHasRole('IwmAllowedToAccessInvoicingProposalsWhereHandler'),
            this.roleService.userHasRole('IwmAllowedToCreateCustomerInvoices')])
                .subscribe((results: [Params, boolean, boolean, boolean, boolean]) => {
                    this.searchId = results[0]['id'];
                    this.searchTarget = results[0]['target'];
                    if (results[1]) {
                        this.accessToCollectionInvoicingProposals = results[1];
                    }
                    if (results[1] || results[3]) {
                        this.accessToInvoicingProposals = results[1] || results[3];
                    }
                    if (results[2]) {
                        this.accessToTenderings = results[2];
                    }
                    if (results[4]) {
                        this.accessToEndCustomerInvoice = results[4];
                    }
                    if (!this.searchTarget) {
                        this.openTarget();
                    } else {
                        this.openSpecificTarget();
                    }
                }));
    }

    ngOnInit() { }

    ngOnDestroy() {
        this.subscriptions.forEach(x => x.unsubscribe());
    }

    public openTarget() {

        const taskQuery = new Subject<string>();
        const womTaskQuery = new Subject<string>();
        const womInvoicingProposalQuery = new Subject<string>();
        const worksiteQuery = new Subject<string>();
        const invoicingProposalQuery = new Subject<string>();
        const endCustomerInvoiceQuery = new Subject<string>();
        const planQuery = new Subject<string>();
        const tenderingQuery = new Subject<string>();
        const collectionInvoicingProposalQuery = new Subject<string>();

        const subjectsToWait: Array<Subject<string>> = new Array<Subject<string>>();
        subjectsToWait.push(taskQuery);
        subjectsToWait.push(womTaskQuery);
        subjectsToWait.push(worksiteQuery);
        subjectsToWait.push(planQuery);

        if (this.accessToInvoicingProposals) {
            subjectsToWait.push(invoicingProposalQuery);
            subjectsToWait.push(womInvoicingProposalQuery);
        }
        if (this.accessToCollectionInvoicingProposals) {
            subjectsToWait.push(collectionInvoicingProposalQuery);
        }
        if (this.accessToTenderings) {
            subjectsToWait.push(tenderingQuery);
        }
        if (this.accessToEndCustomerInvoice) {
            subjectsToWait.push(endCustomerInvoiceQuery);
        }



        forkJoin(subjectsToWait)
            .subscribe(results => {
                let url = '';
                results.forEach(resultUrl => {
                    if (resultUrl !== '') {
                        url = resultUrl;
                    }
                });

                if (url !== '') {
                    this.router.navigate([url], {
                        replaceUrl: true
                    });
                } else {
                    this.searchReady = true;
                    this.cdRef.detectChanges();
                }
            });

        // emit initial values so that forkJoin will fire after each of these is completed
        taskQuery.next('');
        womTaskQuery.next('');
        worksiteQuery.next('');
        invoicingProposalQuery.next('');
        endCustomerInvoiceQuery.next('');
        planQuery.next('');
        womInvoicingProposalQuery.next('');
        tenderingQuery.next('');
        collectionInvoicingProposalQuery.next('');

        const iwmId = parseInt(this.searchId, 10);
        if (isNaN(iwmId)) {
            taskQuery.complete();
            womTaskQuery.complete();
            worksiteQuery.complete();
            invoicingProposalQuery.complete();
            endCustomerInvoiceQuery.complete();
            planQuery.complete();
            womInvoicingProposalQuery.complete();
            tenderingQuery.complete();
            collectionInvoicingProposalQuery.complete();
        } else {
            this.appComponentService.doesTaskExist(iwmId)
                .subscribe(response => {
                    const task = response['value'][0];
                    if (task) {
                        taskQuery.next(`Tasks/${task.Id}/Details`);
                    }
                    taskQuery.complete();
                });
            this.appComponentService.doesWomTaskExist(iwmId)
                .subscribe(response => {
                    const task = response['value'][0];
                    if (task) {
                        womTaskQuery.next(`Tasks/Wom/${task.TaskId}`);
                    }
                    womTaskQuery.complete();
                });
            this.worksiteBasicInfoService.getWorksiteBasicInfoById(iwmId)
                .subscribe(response => {
                    const worksite = response['value'][0];
                    if (worksite) {
                        worksiteQuery.next(`Worksites/${worksite.Id}/Details`);
                    }
                    worksiteQuery.complete();
                });
            this.planBasicInfoService.getPlanBasicInfoByIwmId(iwmId)
                .subscribe(response => {
                    const plan = response['value'][0];
                    if (plan) {
                        planQuery.next(`Plans/${plan.Id}/Details`);
                    }
                    planQuery.complete();
                });

            if (this.accessToInvoicingProposals) {
                this.invoicingProposalBasicInfoService.getInvoicingProposalBasicInfoById(iwmId)
                    .subscribe(response => {
                        const invoicingproposal = response['value'][0];
                        if (invoicingproposal) {
                            invoicingProposalQuery.next(`InvoicingProposals/${invoicingproposal.Id}/Details`);
                        }
                        invoicingProposalQuery.complete();
                    });

                this.invoicingProposalBasicInfoService.getWomInvoicingProposalById(iwmId)
                    .subscribe(response => {
                        const invoicingproposal = response['value'][0];
                        if (invoicingproposal) {
                            womInvoicingProposalQuery.next(`InvoicingProposals/Wom/${invoicingproposal.Id}`);
                        }
                        womInvoicingProposalQuery.complete();
                    });
            }

            if (this.accessToEndCustomerInvoice) {
                this.endCustomerInvoiceService.getEndCustomerInvoiceById(iwmId)
                    .subscribe(response => {
                        const endCustomerInvoice = response['value'][0];
                        if (endCustomerInvoice) {
                            endCustomerInvoiceQuery.next(`EndCustomerInvoices/${endCustomerInvoice.Id}/Details`);
                        }
                        endCustomerInvoiceQuery.complete();
                    });
            }

            if (this.accessToCollectionInvoicingProposals) {
                this.collectionInvoicingProposalBasicInfoService.getCollectionInvoicingProposalBasicInfoById(iwmId)
                    .subscribe(response => {
                        const collectioninvoicingproposal = response['value'][0];
                        if (collectioninvoicingproposal) {
                            collectionInvoicingProposalQuery.next(`InvoicingProposals/Collection/${collectioninvoicingproposal.Id}`);
                        }
                        collectionInvoicingProposalQuery.complete();
                    });
            }
            else {
                collectionInvoicingProposalQuery.complete();
            }
            if (this.accessToTenderings) {
                this.tenderingBasicInfoService.getTenderingBasicInfoByIwmId(iwmId)
                    .subscribe(response => {
                        const tendering = response['value'][0];
                        if (tendering) {
                            tenderingQuery.next(`Tenderings/${tendering.Id}/Details`);
                        }
                        tenderingQuery.complete();
                    });
            }
        }
    }

    private openSpecificTarget() {
        const iwmId = +this.searchId;
        if (isNaN(iwmId)) {
            this.searchReady = true;
            this.cdRef.detectChanges();
        } else {
            switch (this.searchTarget) {
                case 'Task':
                    this.appComponentService.doesTaskExist(iwmId)
                        .subscribe(response => {
                            const task = response['value'][0];
                            if (task) {
                                this.router.navigate([`Tasks/${task.Id}/Details`], {
                                    replaceUrl: true
                                });
                            } else {
                                this.searchReady = true;
                                this.cdRef.detectChanges();
                            }
                        });
                    break;

                case 'WomTask':
                    this.appComponentService.doesWomTaskExist(iwmId)
                        .subscribe(response => {
                            const task = response['value'][0];
                            if (task) {
                                this.router.navigate([`Tasks/Wom/${task.TaskId}`], {
                                    replaceUrl: true
                                });
                            } else {
                                this.searchReady = true;
                                this.cdRef.detectChanges();
                            }
                        });
                    break;

                case 'Worksite':
                    this.worksiteBasicInfoService.getWorksiteBasicInfoById(iwmId)
                        .subscribe(response => {
                            const worksite = response['value'][0];
                            if (worksite) {
                                this.router.navigate([`Worksites/${worksite.Id}/Details`], {
                                    replaceUrl: true
                                });
                            } else {
                                this.searchReady = true;
                                this.cdRef.detectChanges();
                            }
                        });
                    break;

                case 'Plan':
                    this.planBasicInfoService.getPlanBasicInfoByIwmId(iwmId)
                        .subscribe(response => {
                            const plan = response['value'][0];
                            if (plan) {
                                this.router.navigate([`Plans/${plan.Id}/Details`], {
                                    replaceUrl: true
                                });
                            } else {
                                this.searchReady = true;
                                this.cdRef.detectChanges();
                            }
                        });
                    break;

                case 'InvoicingProposal':
                    if (this.accessToInvoicingProposals) {
                        this.invoicingProposalBasicInfoService.getInvoicingProposalBasicInfoById(iwmId)
                            .subscribe(response => {
                                const invoicingproposal = response['value'][0];
                                if (invoicingproposal) {
                                    this.router.navigate([`InvoicingProposals/${invoicingproposal.Id}/Details`], {
                                        replaceUrl: true
                                    });
                                } else {
                                    this.searchReady = true;
                                    this.cdRef.detectChanges();
                                }
                            });
                    }
                    break;


                case 'EndCustomerInvoice':
                    if (this.accessToEndCustomerInvoice) {
                        this.endCustomerInvoiceService.getEndCustomerInvoiceById(iwmId)
                            .subscribe(response => {
                                const endCustomerInvoice = response['value'][0];
                                if (endCustomerInvoice) {
                                    this.router.navigate([`EndCustomerInvoices/${endCustomerInvoice.Id}/Details`], {
                                        replaceUrl: true
                                    });
                                } else {
                                    this.searchReady = true;
                                    this.cdRef.detectChanges();
                                }
                            });
                    }
                    break;

                case 'WomInvoicingProposal':
                    if (this.accessToInvoicingProposals) {
                        this.invoicingProposalBasicInfoService.getWomInvoicingProposalById(iwmId)
                            .subscribe(response => {
                                const invoicingproposal = response['value'][0];
                                if (invoicingproposal) {
                                    this.router.navigate([`InvoicingProposals/Wom/${invoicingproposal.Id}`], {
                                        replaceUrl: true
                                    });
                                } else {
                                    this.searchReady = true;
                                    this.cdRef.detectChanges();
                                }
                            });
                    }
                    break;

                case 'Tendering':
                    if (this.accessToTenderings) {
                        this.tenderingBasicInfoService.getTenderingBasicInfoByIwmId(iwmId)
                            .subscribe(response => {
                                const tendering = response['value'][0];
                                if (tendering) {
                                    this.router.navigate([`Tenderings/${tendering.Id}/Details`], {
                                        replaceUrl: true
                                    });
                                } else {
                                    this.searchReady = true;
                                    this.cdRef.detectChanges();
                                }
                            });
                    }
                    break;

                default:
                    // Target not specified or invalid. Show the search ready layout
                    this.searchReady = true;
                    this.cdRef.detectChanges();
                    break;
            }
        }
    }
}
